import React, { useRef, useMemo } from "react";

import { Box, Typography } from "@mui/material";
import { useReportGeneratorContext } from "components/Report-generator/context/ReportGenerator-context";
import type { CardConfig, MacroBriefAvailableIndicator, MacroBriefCardConfig } from "store/types";

import { CheckRow } from "./CheckRow";
import { SectionLabel } from "./SectionLabel";

interface Props {
  config: MacroBriefCardConfig;
  onUpdateConfig: (c: CardConfig) => void;
}

export const MacroBriefSettings: React.FC<Props> = ({ config, onUpdateConfig }) => {
  const { macroBriefIndicators } = useReportGeneratorContext();

  // Ref keeps the latest indicators in sync so rapid clicks don't read stale props
  const indicatorsRef = useRef(config.indicators);
  indicatorsRef.current = config.indicators;

  const grouped = useMemo(() => {
    const map = new Map<string, MacroBriefAvailableIndicator[]>();
    for (const ind of macroBriefIndicators) {
      const group = map.get(ind.category) || [];
      group.push(ind);
      map.set(ind.category, group);
    }
    return map;
  }, [macroBriefIndicators]);

  return (
    <Box>
      {[...grouped.entries()].map(([category, items]) => (
        <Box key={category} sx={{ mb: 1.5 }}>
          <SectionLabel>{category}</SectionLabel>
          {items.map((ind) => {
            const checked = config.indicators.includes(ind.key);
            return (
              <CheckRow
                key={ind.key}
                label={ind.label}
                checked={checked}
                onChange={(v) => {
                  const current = indicatorsRef.current;
                  const next: string[] = v
                    ? [...current, ind.key]
                    : current.filter((k) => k !== ind.key);
                  indicatorsRef.current = next;
                  onUpdateConfig({ ...config, indicators: next });
                }}
              />
            );
          })}
        </Box>
      ))}
      {macroBriefIndicators.length === 0 && (
        <Typography sx={{ fontSize: 12, color: "#9CA3AF", py: 2 }}>No indicators available</Typography>
      )}
    </Box>
  );
};
